import { noop } from 'lodash';
import { Dispatch, ReactNode, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Cell,
  CellProps,
  Column,
  HeaderGroup,
  HeaderProps,
  Hooks,
  Renderer,
  Row,
  TableInstance,
  TableState,
  TableToggleCommonProps,
  usePagination,
  UsePaginationInstanceProps,
  UsePaginationState,
  useRowSelect,
  useTable,
} from 'react-table';

import { Pagination } from '@/lib/components/Pagination';
import { Select } from '@/lib/components/Select';
import { ItemSelectProps } from '@/lib/components/Select/Select';
import { BigColumnSkeleton } from '@/lib/components/Skeletons/ColumnSkeleton';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import { IndeterminateCheckbox } from './IndeterminateCheckbox';

export type TableProps<T> = {
  columns: Column<object>[];
  data: T[] | number[] | undefined;
  onRowClick?: (obj: T) => void;
  withCheckbox?: boolean;
  setSelectedRows?: Dispatch<SetStateAction<number[]>>;
  withScrollbar?: boolean;
  noNegativeMargin?: boolean;
  classNamePagination?: string;
  withPagination?: {
    totalPages?: number;
    fetchData: ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => void;
  };
  orderBy?: {
    items: ItemSelectProps[];
    onSelect: (element: ItemSelectProps, current: string) => void;
  };
  children?: ReactNode | ReactNode;
  isLoading?: boolean;
  gotoPageIndex?: number;
  emptyScreen?: {
    totalCount: number;
    isEmptySegment?: boolean;
    noResult: ReactNode | ReactNode;
    noData: ReactNode | ReactNode;
    noContactSegment?: ReactNode | ReactNode;
    noPerms?: ReactNode | ReactNode;
    perms?: string | undefined;
  };
  toggleAllRowsSelectedValue?: boolean; // false => unselect all items. true => select all items
  isPaginateOnOverflow?: boolean;
  overflow?: 'auto' | 'hidden' | 'visible' | 'scroll' | 'overflow-x-auto' | 'overflow-y-auto';
  paginationWithPadding?: boolean;
  lastPageViewed?: number;
};

interface SelectionRow<D extends object = {}> extends Row<D> {
  getToggleRowSelectedProps: () => TableToggleCommonProps;
}

export function Table<T>({
  columns,
  data,
  onRowClick,
  withCheckbox,
  setSelectedRows,
  withPagination,
  withScrollbar,
  orderBy,
  children,
  isLoading,
  gotoPageIndex,
  emptyScreen,
  isPaginateOnOverflow,
  toggleAllRowsSelectedValue = false,
  overflow = 'overflow-x-auto',
  noNegativeMargin = false,
  classNamePagination,
  paginationWithPadding,
  lastPageViewed,
}: TableProps<T>) {
  const { t } = useTranslation();
  const useCheckboxes = (hooks: Hooks) => {
    withCheckbox &&
      hooks.visibleColumns.push((columnList: Column[]) => [
        {
          id: 'selection',
          width: '20px',
          className: 'checkbox',
          // eslint-disable-next-line @typescript-eslint/ban-types
          Header: ({ getToggleAllRowsSelectedProps }: HeaderProps<{}>) => (
            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          ),
          Cell: (props: CellProps<{}>) => {
            const row: SelectionRow = props.row as SelectionRow;
            return (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            );
          },
        },
        ...columnList,
      ]);
  };

  const {
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, selectedRowIds },
    toggleAllRowsSelected,
    getTableProps,
    getTableBodyProps,
  } = useTable(
    {
      columns: columns || [],
      data: (data || []) as unknown as readonly object[],
      manualPagination: true,
      pageCount: withPagination?.totalPages,
      initialState: {
        pageIndex: lastPageViewed ? lastPageViewed - 1 : 0,
      } as Partial<UsePaginationState<object>> & Partial<TableState<object>>,
    },
    usePagination,
    useRowSelect,
    useCheckboxes
  ) as TableInstance<object> & UsePaginationInstanceProps<object>;

  useEffect(() => {
    withPagination?.fetchData && withPagination.fetchData({ pageIndex, pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withPagination?.fetchData, pageIndex, pageSize]);

  useEffect(() => {
    if (!gotoPageIndex) {
      gotoPage(gotoPageIndex ? gotoPageIndex : 0);
    }
  }, [withPagination?.fetchData, gotoPageIndex, gotoPage]);

  const AddExtraRow = withPagination || orderBy || children;

  useEffect(() => {
    if (setSelectedRows) setSelectedRows(Object.keys(selectedRowIds).map((i) => Number(i)));
  }, [selectedRowIds, setSelectedRows]);

  useEffect(() => {
    toggleAllRowsSelected(toggleAllRowsSelectedValue);
  }, [toggleAllRowsSelected, toggleAllRowsSelectedValue]);

  const validationEmptyScreen = () => {
    const hasNoData =
      data &&
      data.length === 0 &&
      emptyScreen &&
      emptyScreen.totalCount === 0 &&
      !emptyScreen.isEmptySegment &&
      !emptyScreen.perms;
    const hasNoContactSegmentOrPerms =
      (emptyScreen && emptyScreen.isEmptySegment) || (emptyScreen && emptyScreen.perms);
    const hasDataAndNotPerms = data && data.length > 0 && !emptyScreen?.perms;

    let emptyScreenMessage = emptyScreen && emptyScreen.noResult;

    if (hasNoData) {
      emptyScreenMessage = emptyScreen.noData;
    } else if (hasNoContactSegmentOrPerms) {
      emptyScreenMessage = emptyScreen.noContactSegment || emptyScreen.noPerms;
    } else if (hasDataAndNotPerms) {
      return null;
    }

    return (
      <Flex alignment="center" itemAlignment="center">
        {emptyScreenMessage}
      </Flex>
    );
  };

  return (
    <>
      {validationEmptyScreen() ?? (
        <div
          className={`${!noNegativeMargin && '-mx-4 -mt-4'} ${overflow && overflow} ${
            withScrollbar && 'visible-scrollbar max-h-[650px]'
          }`}
        >
          <Flex column withGap>
            {AddExtraRow && (
              <Flex>
                <div className="pl-1">
                  {(children || orderBy) && (
                    <Flex noGrow withGap gapSize="small">
                      {children && children}
                      {orderBy && (
                        <Flex withGap>
                          <p className="whitespace-nowrap">{t('CONTACTS_TABLE.orderBy')} </p>
                          <Select plain itemList={orderBy.items} onSelect={orderBy.onSelect} />
                        </Flex>
                      )}
                    </Flex>
                  )}
                </div>
                <div
                  className={paginationWithPadding ? 'px-3 lg:px-4 2xl:px-12' : classNamePagination}
                >
                  {withPagination && (
                    <Pagination
                      functions={{
                        canPreviousPage,
                        previousPage,
                        canNextPage,
                        nextPage,
                        pageCount,
                        pageIndex,
                        pageOptions,
                        pageSize,
                        setPageSize,
                      }}
                    />
                  )}
                </div>
              </Flex>
            )}

            {isLoading && (
              <Flex>
                {columns.map((_, i) => (
                  <BigColumnSkeleton key={i?.toString()} />
                ))}
              </Flex>
            )}
            {!isLoading && (
              <table
                className="min-w-full table-auto divide-y divide-gray-300"
                {...getTableProps()}
              >
                <thead className="bg-emblueLightGray">
                  {headerGroups.map((headerGroup: HeaderGroup<object>) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column: HeaderGroup<object>) => (
                        <th
                          className="py-4 text-14 font-medium text-gray-700 lg:px-2 xl:px-4 2xl:px-6"
                          scope="col"
                          {...column.getHeaderProps({
                            style: {
                              minWidth: column.minWidth,
                              width: column.width,
                              maxWidth: column.maxWidth,
                            },
                          })}
                        >
                          {column.render('Header')}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-200" {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        className={`${
                          onRowClick && 'cursor-pointer'
                        } hover:bg-slate-50 active:bg-slate-100`}
                        {...row.getRowProps()}
                      >
                        {row.cells.map((cell: Cell<object, unknown>) => {
                          return (
                            <td
                              className="text-sm whitespace-nowrap py-4 text-gray-900 lg:px-2 xl:px-4 2xl:px-6"
                              onClick={() =>
                                onRowClick && cell.column.id !== 'selection'
                                  ? onRowClick(row.values as T)
                                  : noop
                              }
                              {...cell.getCellProps({
                                style: {
                                  minWidth: cell.column.minWidth,
                                  width: cell.column.width,
                                  maxWidth: cell.column.maxWidth,
                                },
                              })}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Flex>
          {data && ((data.length >= 10 && !isPaginateOnOverflow) || data.length >= 11) && (
            <div className="mt-2 bg-slate-50 pb-12 pt-4">
              <Flex alignment="end">
                <div
                  className={paginationWithPadding ? 'px-3 lg:px-4 2xl:px-12' : classNamePagination}
                >
                  {withPagination && (
                    <Pagination
                      resetScroll
                      functions={{
                        canPreviousPage,
                        previousPage,
                        canNextPage,
                        nextPage,
                        pageCount,
                        pageIndex,
                        pageOptions,
                        pageSize,
                        setPageSize,
                      }}
                    />
                  )}
                </div>
              </Flex>
            </div>
          )}
        </div>
      )}
    </>
  );
}
